import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const exportAppraisalPDF = async (elementId, fileName = 'appraisal-report.pdf', openPrint = false) => {
  // Try multiple ways to find the element
  let element = document.getElementById(elementId);

  if (!element) {
    console.warn(`Element with ID "${elementId}" not found, trying alternatives...`);
    element = document.querySelector('.report-shell__page') || 
              document.querySelector('[data-report-content]');
  }
  
  // ✅ If still not found, throw error
  if (!element) {
    console.error('❌ Element not found');
    throw new Error(`Element "${elementId}" not found in DOM`);
  }
  
  if (!element) {
    console.warn(`Element with ID "${elementId}" not found, trying alternatives...`);
    element = document.querySelector('[data-report-content]') || 
              document.querySelector('.appraisal-content') ||
              document.querySelector('#appraisal-content') ||
              document.querySelector('#my-objectives-content') ||
              document.querySelector('.my-objectives-content') ||
              document.querySelector('.my-objectives-list') ||
              document.querySelector('.report-shell__page');
  }

  if (!element) {
    console.error('❌ Element not found');
    throw new Error(`Element "${elementId}" not found in DOM`);
  }

  if (!(element instanceof HTMLElement)) {
    console.error('❌ Element is not a valid HTMLElement');
    throw new Error('Element is not a valid HTMLElement');
  }

  // ✅ NEW: Variables to track UI elements to hide/restore
  let toolbar = null;
  let heroActions = null;
  let pdfLoading = null;
  let originalToolbarDisplay = '';
  let originalHeroActionsDisplay = '';
  let originalLoadingDisplay = '';

  try {
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'flex';

    // ✅ NEW: Hide export-related UI elements BEFORE cloning
    toolbar = document.querySelector('.report-shell__toolbar');
    heroActions = document.querySelector('.report-shell__hero-actions');
    pdfLoading = document.getElementById('pdf-loading');
    
    originalToolbarDisplay = toolbar?.style.display || '';
    originalHeroActionsDisplay = heroActions?.style.display || '';
    originalLoadingDisplay = pdfLoading?.style.display || '';
    
    if (toolbar) toolbar.style.display = 'none';
    if (heroActions) heroActions.style.display = 'none';
    if (pdfLoading) pdfLoading.style.display = 'none';

    // ✅ Log the element being captured
    console.log('📄 Capturing element:', element.id || element.className || 'unknown');
    console.log('📄 Element has children:', element.children.length);

    // ✅ Ensure all content is expanded
    await expandAllContent(element);
    await new Promise(resolve => setTimeout(resolve, 500));

    // ✅ CRITICAL: Convert ALL images to data URLs before capture
    await convertAllImagesToDataURLs(element);

    // ✅ Log images after conversion
    const images = element.querySelectorAll('img');
    console.log(`📸 Found ${images.length} images after conversion`);
    images.forEach((img, i) => {
      console.log(`  Image ${i}: src starts with data: ${img.src?.startsWith('data:')}, alt: ${img.alt || 'no alt'}`);
    });

    const rect = element.getBoundingClientRect();
    const width = element.scrollWidth || rect.width || 1200;
    const height = element.scrollHeight || rect.height || 0;

    if (height === 0) {
      throw new Error('Content is empty');
    }

    console.log(`📄 Capturing content: ${width}x${height}px`);

    // ✅ Create a temporary container with proper positioning
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = width + 'px';
    tempContainer.style.overflow = 'visible';
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.padding = '0';
    tempContainer.style.margin = '0';
    document.body.appendChild(tempContainer);

    // ✅ Clone the element and ensure it's properly prepared
    const clone = element.cloneNode(true);
    
    // ✅ Remove any negative margins or padding that might cause cut-off
    clone.style.margin = '0';
    clone.style.padding = '20px'; // Add some padding
    clone.style.position = 'relative';
    clone.style.top = '0';
    
    // ✅ Ensure the clone has the data URL version of the logo
    await ensureDataURLImagesInClone(clone);
    
    // ✅ Hide UI-only elements (buttons, toolbars, etc.) in the clone before capture
    ensureAllContentVisible(clone);
    
    // ✅ AGGRESSIVE: Completely remove toolbar and hero actions from clone
    const cloneToolbar = clone.querySelector('.report-shell__toolbar');
    const cloneHeroActions = clone.querySelector('.report-shell__hero-actions');
    const cloneHero = clone.querySelector('.report-shell__hero');
    const cloneLoading = clone.querySelector('#pdf-loading');
    
    if (cloneToolbar) cloneToolbar.remove();
    if (cloneHeroActions) cloneHeroActions.remove();
    if (cloneHero) cloneHero.remove();
    if (cloneLoading) cloneLoading.remove();
    
    tempContainer.appendChild(clone);

    // Wait for the clone to render
    await new Promise(resolve => setTimeout(resolve, 500));

    // ✅ Log the clone's content
    const cloneImages = clone.querySelectorAll('img');
    console.log(`📸 Clone has ${cloneImages.length} images`);
    cloneImages.forEach((img, i) => {
      console.log(`  Clone Image ${i}: src starts with data: ${img.src?.startsWith('data:')}`);
    });

    // ✅ Try to find the logo specifically
    //const logo = clone.querySelector('.report-shell__company-logo');
    const logo = element.querySelector('.report-shell__company-logo');
    if (logo) {
      console.log('✅ Logo found in clone:', logo.src?.substring(0, 50) + '...');
    } else {
      console.warn('⚠️ Logo NOT found in clone!');
    }

    const canvas = await html2canvas(clone, {
      scale: 2.5,
      useCORS: true,
      allowTaint: true,
      logging: true, // Enable logging for debugging
      backgroundColor: '#ffffff',
      width: width,
      height: height,
      imageTimeout: 0,
      removeContainer: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: width,
      windowHeight: height,
      onclone: (document, clonedElement) => {
        console.log('🔍 html2canvas onclone triggered');
        
        // Ensure all elements are visible
        const allElements = clonedElement.querySelectorAll('*');
        allElements.forEach(el => {
          if (el.style) {
            el.style.visibility = 'visible';
            el.style.opacity = '1';
            if (el.tagName === 'IMG') {
              el.style.display = 'block';
              el.style.visibility = 'visible';
              el.style.opacity = '1';
              // Force image to load
              const src = el.getAttribute('src');
              if (src) {
                el.setAttribute('src', src);
              }
            }
          }
        });
        
        // Specifically ensure company logo is visible
        const logoEl = clonedElement.querySelector('.report-shell__company-logo');
        if (logoEl) {
          console.log('✅ Logo found in onclone, ensuring visibility');
          logoEl.style.display = 'block';
          logoEl.style.visibility = 'visible';
          logoEl.style.opacity = '1';
          logoEl.style.maxHeight = '60px';
          logoEl.style.maxWidth = '100px';
          logoEl.style.width = 'auto';
          logoEl.style.height = 'auto';
          logoEl.style.margin = '0 auto 4px auto';
        } else {
          console.warn('⚠️ Logo NOT found in onclone!');
        }
      }
    });

    // Clean up temporary container
    document.body.removeChild(tempContainer);

    if (!canvas || !canvas.getContext) {
      throw new Error('Failed to create canvas');
    }

    console.log('✅ Canvas created successfully');

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    const totalPages = Math.ceil(imgHeight / pdfHeight);

    console.log(`📄 Splitting into ${totalPages} pages`);

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) {
        pdf.addPage();
      }

      const yOffset = page * pdfHeight;
      const remainingHeight = Math.min(pdfHeight, imgHeight - yOffset);

      const srcY = (yOffset / imgHeight) * canvas.height;
      const srcHeight = (remainingHeight / imgHeight) * canvas.height;

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = srcHeight;
      const ctx = pageCanvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(
        canvas,
        0, srcY, canvas.width, srcHeight,
        0, 0, canvas.width, srcHeight
      );

      const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.85);
      pdf.addImage(pageImgData, 'JPEG', 0, 0, pdfWidth, remainingHeight);
      
      pageCanvas.width = 0;
      pageCanvas.height = 0;
    }

    // Save or print
    if (openPrint) {
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = function() {
          setTimeout(function() {
            printWindow.print();
          }, 1000);
        };
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 5000);
      } else {
        pdf.save(fileName);
        alert('Print popup was blocked. The PDF has been downloaded. Please open and print it manually.');
      }
    } else {
      pdf.save(fileName);
    }

    // ✅ NEW: Restore toolbar and other UI elements
    if (toolbar) toolbar.style.display = originalToolbarDisplay || '';
    if (heroActions) heroActions.style.display = originalHeroActionsDisplay || '';
    if (pdfLoading) pdfLoading.style.display = originalLoadingDisplay || 'none';

    if (loadingElement) loadingElement.style.display = 'none';
    
    console.log('✅ PDF generated successfully');
  } catch (error) {
    console.error('❌ PDF export failed:', error);
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'none';
    
    // ✅ NEW: Restore toolbar and other UI elements on error
    if (toolbar) toolbar.style.display = originalToolbarDisplay || '';
    if (heroActions) heroActions.style.display = originalHeroActionsDisplay || '';
    if (pdfLoading) pdfLoading.style.display = originalLoadingDisplay || 'none';
    
    alert(`Failed to generate PDF. Please try again. Error: ${error.message}`);
    throw error;
  }
};

// ✅ Convert ALL images to data URLs before capture
const convertAllImagesToDataURLs = async (element) => {
  const images = element.querySelectorAll('img');
  if (images.length === 0) {
    console.log('ℹ️ No images found to convert');
    return;
  }

  console.log(`🔄 Converting ${images.length} images to data URLs...`);
  
  const loadPromises = Array.from(images).map((img) => {
    return new Promise((resolve) => {
      // If already a data URL, skip
      if (img.src && img.src.startsWith('data:')) {
        resolve();
        return;
      }

      // If image is loaded, convert it
      if (img.complete && img.naturalWidth > 0) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          img.src = dataUrl;
          console.log('✅ Image converted to data URL');
          resolve();
        } catch (e) {
          console.warn('Failed to convert image:', e);
          resolve();
        }
        return;
      }

      // Otherwise, wait for it to load
      const timeout = setTimeout(() => {
        resolve();
      }, 5000);

      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          img.src = dataUrl;
          console.log('✅ Image converted to data URL');
        } catch (e) {
          console.warn('Failed to convert image:', e);
        }
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timeout);
        resolve();
      };
    });
  });

  await Promise.all(loadPromises);
  console.log('✅ All images converted to data URLs');
};

// ✅ Ensure data URL images are in the clone
const ensureDataURLImagesInClone = async (clone) => {
  const images = clone.querySelectorAll('img');
  if (images.length === 0) return;

  console.log(`📸 Ensuring ${images.length} images have data URLs in clone...`);
  
  // Find the original images to copy data URLs from
  const originalImages = document.querySelectorAll('img');
  const originalSrcMap = new Map();
  
  originalImages.forEach(img => {
    if (img.src && img.src.startsWith('data:')) {
      // Use a unique identifier to match images
      const alt = img.getAttribute('alt') || '';
      const className = img.className || '';
      const key = `${alt}_${className}`;
      originalSrcMap.set(key, img.src);
      console.log(`  Stored data URL for: ${key}`);
    }
  });

  clone.querySelectorAll('img').forEach((img, index) => {
    // If the image already has a data URL, skip
    if (img.src && img.src.startsWith('data:')) {
      console.log(`  Image ${index} already has data URL`);
      return;
    }
    
    // Try to find matching original image by alt or className
    const alt = img.getAttribute('alt') || '';
    const className = img.className || '';
    const key = `${alt}_${className}`;
    
    if (originalSrcMap.has(key)) {
      img.src = originalSrcMap.get(key);
      console.log(`✅ Restored data URL for image: ${alt || className || index}`);
    } else {
      console.warn(`⚠️ No data URL found for image: ${alt || className || index}`);
    }
  });
};


// Helper function to expand all collapsible content
const expandAllContent = (element) => {
  console.log('🔍 Expanding all content...');
  
  if (!element) return;

   // ✅ Ensure the logo container is visible
  const logoContainer = element.querySelector('.report-shell__company-header');
  if (logoContainer) {
    logoContainer.style.display = 'flex';
    logoContainer.style.visibility = 'visible';
    logoContainer.style.opacity = '1';
    logoContainer.style.minHeight = '80px';
  }
  
  // ✅ Ensure the logo image is visible
  const logoImg = element.querySelector('.report-shell__company-logo');
  if (logoImg) {
    logoImg.style.display = 'block';
    logoImg.style.visibility = 'visible';
    logoImg.style.opacity = '1';
    logoImg.style.maxHeight = '60px';
    logoImg.style.maxWidth = '100px';
    logoImg.style.width = 'auto';
    logoImg.style.height = 'auto';
    logoImg.style.margin = '0 auto 4px auto';
    console.log('✅ Logo found and made visible in expandAllContent');
  } else {
    console.warn('⚠️ Logo NOT found in element during expandAllContent');
  }
  
  // Find all collapsed panels
  const collapsedPanels = element.querySelectorAll(
    '.apr-obj-panel--hidden, ' +
    '[class*="panel--hidden"], ' +
    '.objective-details, ' +
    '.apr-obj-panel, ' +
    '.collapsible-content, ' +
    '.hidden-content'
  );
  
  collapsedPanels.forEach(panel => {
    try {
      panel.classList.remove('apr-obj-panel--hidden');
      panel.style.display = 'block';
      panel.style.maxHeight = 'none';
      panel.style.overflow = 'visible';
      panel.style.visibility = 'visible';
      panel.style.opacity = '1';
    } catch (e) {
      // Ignore individual errors
    }
  });

  // Find all expandable buttons and click them
  const expandButtons = element.querySelectorAll(
    '[class*="expand"], ' +
    '[class*="toggle"], ' +
    '[aria-expanded="false"], ' +
    '.view-details-btn'
  );
  
  expandButtons.forEach(button => {
    try {
      button.click();
      button.setAttribute('aria-expanded', 'true');
    } catch (e) {
      button.setAttribute('aria-expanded', 'true');
    }
  });

  // Force show all objective panels
  const objPanels = element.querySelectorAll('.apr-obj-panel, .objective-panel');
  objPanels.forEach(panel => {
    try {
      panel.style.display = 'block';
      panel.style.maxHeight = 'none';
      panel.style.overflow = 'visible';
      panel.style.visibility = 'visible';
      panel.style.opacity = '1';
    } catch (e) {
      // Ignore
    }
  });

  // Ensure all objective cards show details
  const objectiveCards = element.querySelectorAll('.objective-card, .apr-objective-card');
  objectiveCards.forEach(card => {
    try {
      const details = card.querySelector('.objective-details, .apr-obj-details');
      if (details) {
        details.style.display = 'block';
        details.style.maxHeight = 'none';
        details.style.overflow = 'visible';
      }
    } catch (e) {
      // Ignore
    }
  });

  console.log('✅ Content expansion complete');
};

// Helper to ensure all content is visible in the clone
const ensureAllContentVisible = (element) => {
  if (!element) return;

  // ✅ Ensure the logo container is visible
  const logoContainer = element.querySelector('.report-shell__company-header');
  if (logoContainer) {
    logoContainer.style.display = 'flex';
    logoContainer.style.visibility = 'visible';
    logoContainer.style.opacity = '1';
    logoContainer.style.minHeight = '80px';
  }
  
  // ✅ Ensure company logo is visible
  const logo = element.querySelector('.report-shell__company-logo');
  if (logo) {
    logo.style.display = 'block';
    logo.style.visibility = 'visible';
    logo.style.opacity = '1';
    logo.style.maxHeight = '60px';
    logo.style.maxWidth = '100px';
    logo.style.width = 'auto';
    logo.style.height = 'auto';
    logo.style.margin = '0 auto 4px auto';
    console.log('✅ Logo found and made visible in clone');
  } else {
    console.warn('⚠️ Logo NOT found in clone during ensureAllContentVisible');
  }
  
  // Hide UI-only elements that should not appear in the PDF
  const uiElementsToHide = [
    '.report-shell__toolbar',
    '.report-shell__hero',
    '.report-shell__hero-actions',
    '.report-shell__print-footer',
    '.hide-in-print',
    '[data-print-hidden="true"]',
    'button',  // Hide all buttons in PDF
    '.btn',    // Common button classes
    '[role="button"]',
  ];
  uiElementsToHide.forEach(selector => {
    const el = element.querySelector(selector);
    if (el) {
      el.style.display = 'none';
      el.style.visibility = 'hidden';
      el.style.height = '0';
      el.style.padding = '0';
      el.style.margin = '0';
      el.style.border = 'none';
    }
  });

  // ✅ Ensure company logo is visible  
  if (logo) {
    logo.style.display = 'block';
    logo.style.visibility = 'visible';
    logo.style.opacity = '1';
    logo.style.maxHeight = '60px';
    logo.style.maxWidth = '100px';
    logo.style.width = 'auto';
    logo.style.height = 'auto';
    logo.style.margin = '0 auto 4px auto';
  }

  // Ensure company name is visible
  const companyName = element.querySelector('.report-shell__company-name');
  if (companyName) {
    companyName.style.display = 'block';
    companyName.style.visibility = 'visible';
    companyName.style.opacity = '1';
    companyName.style.fontSize = '16px';
    companyName.style.fontWeight = '700';
    companyName.style.color = '#0f5132';
    companyName.style.textAlign = 'center';
  }

  const allElements = element.querySelectorAll('*');
  allElements.forEach(el => {
    try {
      // Force visibility
      el.style.visibility = 'visible';
      el.style.opacity = '1';
      
      // If it's a block element, make sure it's displayed
      if (el.tagName === 'DIV' || el.tagName === 'SECTION' || el.tagName === 'ARTICLE') {
        el.style.display = 'block';
      }
      
      // Remove any max-height restrictions
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
      
      // Ensure text is readable
      if (el.style.fontSize) {
        const size = parseFloat(el.style.fontSize);
        if (size < 12 && size > 0) {
          el.style.fontSize = '14px';
        }
      }
    } catch (e) {
      // Ignore individual errors
    }
  });
};